import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleBanffMotelsHotels() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "banff-motels-and-hotels-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Luxury & Upscale Hotels",
    "section-2": "Mid-Range Hotels",
    "section-3": "Budget-Friendly Motels",
    "section-4": "Local Regulations",
    "section-5": "Practical Travel Tips",
    "section-6": "FAQ",
    "section-7": "Special Section",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.pc.gc.ca/en/pn-np/ab/banff",
      label: "Parks Canada – Banff National Park (Official Website)",
    },
    {
      url: "https://www.banfflakelouise.com/",
      label: "Banff & Lake Louise Tourism (Official)",
    },
    {
      url: "https://www.banff.ca/1002/Maps",
      label: "Town of Banff – Visitor Maps & Transit",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <p> or <li> etc.
  const locations = [
    {
      // Fairmont Banff Springs
      id: "fairmont-banff-springs",
      lat: 51.16405,
      lng: -115.56146,
      popupText: "Fairmont Banff Springs (Luxury Hotel)",
    },
    {
      // Rimrock Resort
      id: "rimrock-resort",
      lat: 51.15163,
      lng: -115.55943,
      popupText: "The Rimrock Resort Hotel (Upscale)",
    },
    {
      // Moose Hotel & Suites
      id: "moose-hotel-suites",
      lat: 51.18065,
      lng: -115.56953,
      popupText: "Moose Hotel & Suites (Mid-Range)",
    },
    {
      // Banff Park Lodge
      id: "banff-park-lodge",
      lat: 51.17917,
      lng: -115.56970,
      popupText: "Banff Park Lodge (Mid-Range)",
    },
    {
      // Mount Royal Hotel
      id: "mount-royal-hotel",
      lat: 51.17622,
      lng: -115.57047,
      popupText: "Mount Royal Hotel (Mid-Range, Historic)",
    },
    {
      // The Dorothy Motel
      id: "dorothy-motel",
      lat: 51.18635,
      lng: -115.55634,
      popupText: "The Dorothy Motel (Budget-Friendly)",
    },
    {
      // Example unique stay: Storm Mountain Lodge (just showcasing)
      id: "storm-mountain-lodge",
      lat: 51.21684,
      lng: -115.96538,
      popupText: "Storm Mountain Lodge (Unique Cabin Experience)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Motels and Hotels in Banff: Where to Stay & Travel Tips</title>
        <meta
          name="description"
          content="A comprehensive guide to motels and hotels in Banff, featuring luxury resorts, budget-friendly motels, local regulations, travel tips, FAQs, and more."
        />
        <meta
          name="keywords"
          content="banff, hotels, motels, travel guide, accommodation, rocky mountains"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/banff-motels-and-hotels-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Motels and Hotels in Banff"
        />
        <meta
          property="og:description"
          content="Discover the best places to stay in Banff: luxury, mid-range, budget motels, plus regulations, tips, and FAQs."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/banff-motels-and-hotels-guide.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/banff-motels-and-hotels-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Motels and Hotels in Banff"
        />
        <meta
          name="twitter:description"
          content="Check out this guide to Banff accommodations, from iconic Fairmont Banff Springs to cozy motels."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/banff-motels-and-hotels-guide.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Banff Hotels and Motels"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Banff, Alberta is a world-class mountain destination famed for its
              rugged peaks, turquoise lakes, and abundant wildlife. Choosing the
              right place to stay can elevate your Banff experience – whether
              you wake up in a historic castle hotel or a cozy budget motel,
              your accommodation sets the tone for your adventure. This guide
              will help you navigate the best hotels and motels in Banff, from
              luxury resorts to affordable lodgings, along with local
              regulations, travel tips, and FAQs to make the most of your Rocky
              Mountain getaway.
            </p>

            {/* SECTION: Luxury & Upscale Hotels */}
            <h2 id="section-1" ref={(el) => setSpotRef("section-1", el)}>
              Luxury & Upscale Hotels
            </h2>
            <p>
              For those seeking a luxurious retreat, Banff offers world-famous
              hotels with top-notch amenities, stunning views, and exceptional
              service.
            </p>

            {/* Fairmont Banff Springs */}
            <p>
              <strong>Fairmont Banff Springs</strong>{" "}
              <Marker
                coordY={51.16405}
                coordX={-115.56146}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Known as the “Castle in the Rockies,” the Fairmont Banff Springs
              (405 Spray Ave, Banff, AB T1L 1J4) offers a historic setting with
              premium amenities, including a full-service spa, golf course,
              multiple restaurants, and stunning mountain views. Room rates
              reflect its high-end status, often exceeding CAD $500 per night in
              peak season.
            </p>

            {/* Rimrock Resort Hotel */}
            <p>
              <strong>The Rimrock Resort Hotel</strong>{" "}
              <Marker
                coordY={51.15163}
                coordX={-115.55943}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Perched on Sulphur Mountain (300 Mountain Ave, Banff, AB T1L
              1J2), this upscale hotel boasts panoramic views, a full spa, and
              upscale dining. It’s within walking distance of the Banff Gondola
              and Upper Hot Springs. Luxury comes with a higher price tag and
              distance from downtown (about 2.5 km uphill), but regular shuttles
              make it manageable.
            </p>

            {/* SECTION: Mid-Range Hotels */}
            <h2 id="section-2" ref={(el) => setSpotRef("section-2", el)}>
              Mid-Range Hotels
            </h2>
            <p>
              If you prefer a comfortable, central stay without the steep luxury
              costs, explore Banff’s mid-range options offering modern amenities
              and easy town access.
            </p>

            {/* Moose Hotel & Suites */}
            <p>
              <strong>Moose Hotel & Suites</strong>{" "}
              <Marker
                coordY={51.18065}
                coordX={-115.56953}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Centrally located at 345 Banff Ave, it features rooftop outdoor
              hot pools, a spa, underground parking, and an on-site Italian
              restaurant. Rates range from CAD $250–$400, depending on season.
            </p>

            {/* Banff Park Lodge */}
            <p>
              <strong>Banff Park Lodge</strong>{" "}
              <Marker
                coordY={51.17917}
                coordX={-115.56970}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Located downtown at 222 Lynx St, near the Bow River. This
              full-service hotel has an indoor pool, hot tub, and on-site
              restaurants, plus eco-friendly initiatives like recycling and
              energy conservation. It’s a short stroll from Banff’s main
              shopping and dining strips.
            </p>

            {/* Mount Royal Hotel */}
            <p>
              <strong>Mount Royal Hotel</strong>{" "}
              <Marker
                coordY={51.17622}
                coordX={-115.57047}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A historic downtown option at 138 Banff Ave. Recently
              renovated, it offers modern rooms, a rooftop lounge with hot tubs,
              and on-site heritage displays. Being right on Banff Avenue can
              bring some noise, but it’s unbeatable for those wanting immediate
              access to local nightlife and restaurants.
            </p>

            {/* SECTION: Budget-Friendly Motels */}
            <h2 id="section-3" ref={(el) => setSpotRef("section-3", el)}>
              Budget-Friendly Motels
            </h2>
            <p>
              Banff can be pricey, but there are still a few wallet-friendly
              options that provide a clean and comfortable stay.
            </p>

            {/* Dorothy Motel */}
            <p>
              <strong>The Dorothy Motel</strong>{" "}
              <Marker
                coordY={51.18635}
                coordX={-115.55634}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Located at 250 Marmot Cres, near the entrance of Banff Avenue,
              it brands itself as Banff’s first “boutique motel.” Simple, modern
              rooms often at half the price of downtown hotels. It’s a 20-minute
              walk or short drive to the center; limited amenities on-site, but
              perfect for road trippers or those traveling with pets.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="section-4" ref={(el) => setSpotRef("section-4", el)}>
              Local Regulations
            </h2>
            <p>
              Visiting Banff means abiding by National Park rules. You’ll need a
              valid Parks Canada pass, and it’s crucial to respect wildlife by
              keeping your distance and never feeding animals. Pet owners must
              leash dogs in town and on trails. The town of Banff enforces quiet
              hours and smoking bylaws; many hotels are entirely smoke-free.
            </p>

            {/* SECTION: Practical Travel Tips */}
            <h2 id="section-5" ref={(el) => setSpotRef("section-5", el)}>
              Practical Travel Tips
            </h2>
            <p>
              For the best accommodation deals, target shoulder seasons like
              April–May or September–October. Book well in advance for summer,
              as hotels fill up fast. Once you’re in Banff, take advantage of
              the walkable downtown and Roam public transit to explore without
              worrying about parking. For a unique experience, consider cabin
              stays like Storm Mountain Lodge{" "}
              <Marker
                coordY={51.21684}
                coordX={-115.96538}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              —a quieter, off-the-beaten-path getaway.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="section-6" ref={(el) => setSpotRef("section-6", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. Do I need a National Park pass if I’m staying in Banff?</h3>
              <p>
                Yes, a valid pass is required for everyone in Banff National
                Park, including hotel guests. You can purchase day passes or
                annual Discovery Passes at the park gate or online. Display it
                on your vehicle dashboard to avoid fines.
              </p>

              <h3>2. Are there pet-friendly accommodations in Banff?</h3>
              <p>
                Many hotels and motels welcome pets, though fees often apply.
                Dogs must be leashed at all times, and you should confirm
                policies before booking. Popular pet-friendly spots include the
                Dorothy Motel and Rimrock Resort.
              </p>

              <h3>3. When is the best time to find cheaper hotel rates?</h3>
              <p>
                Shoulder seasons (spring and fall) typically have lower rates.
                Winter (excluding holidays) can also be cheaper. If you’re
                flexible with dates and can travel mid-week, you’ll often find
                additional savings.
              </p>
            </div>

            {/* SECTION: Special Section */}
            <h2 id="section-7" ref={(el) => setSpotRef("section-7", el)}>
              Special Section: Unique Experiences
            </h2>
            <p>
              Beyond standard hotels and motels, Banff has charming cabin
              retreats like{" "}
              <strong>Storm Mountain Lodge</strong>{" "}
              <Marker
                coordY={51.21684}
                coordX={-115.96538}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (located on Highway 93 near Castle Junction). These secluded
              accommodations offer cozy wood cabins, a restaurant featuring
              locally sourced ingredients, and the chance to disconnect under
              starry skies. Perfect for romantic getaways or tranquil family
              retreats.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Banff’s accommodations cater to every traveler, from the ultimate
              luxury escape to budget-friendly motels or secluded cabins.
              Respect the environment by following local regulations, take
              advantage of mid-week deals or shoulder seasons, and enjoy the
              convenience of the Roam bus system once you arrive. Whether you
              choose a “Castle in the Rockies” or a rustic mountain lodge, make
              sure your stay in Banff is as unforgettable as the park’s
              breathtaking scenery.
            </p>
            <p>
              <em>
                Disclaimer: Always check official resources for the most current
                hotel policies, local regulations, and safety guidelines. Banff
                is in a national park, so adhering to wildlife and conservation
                rules is crucial for preserving the area’s natural beauty.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}